import { useMediaQuery } from '@mantine/hooks';
import { inject, observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { PWAInstallPrompt } from './global/component/PWAInstallPrompt/PWAInstallPrompt.tsx';
import EICFConsentGuard from './global/component/organism/EICFConsentGuard/EICFConsentGuard.tsx';
import { SupportRequestButton } from './global/component/organism/SupportRequestButton/SupportRequestButton.tsx';
import { WorktreeIndicator } from './global/component/WorktreeIndicator/WorktreeIndicator.tsx';
import Shell from './global/component/organism/Shell/Shell.tsx';
import { useNavProvider } from './global/provider/navigationContext.tsx';
import { useSessionProvider } from './global/provider/sessionContext.tsx';
import './i18n';
import AdminDashboardRoute from './pages/AdminDashboard/AdminDashboardRoute.tsx';
import HcpForm from './pages/AdminDashboard/Form/HcpForm.tsx';
import HcpDashboardRoute from './pages/HcpDashboard/HcpDashboardRoute.tsx';
import HcpPatientDashboardRoute from './pages/HcpPatientDashboard/HcpPatientDashboardRoute.tsx';
import ActivateAccount from './pages/Login/ActivateAccount/ActivateAccount.tsx';
import ChangePassword from './pages/Login/ChangePassword/ChangePassword.tsx';
import EmailVerification from './pages/Login/EmailVerification/EmailVerification.tsx';
import ForgotPassword from './pages/Login/ForgotPassword/ForgotPassword.tsx';
import LoginForm from './pages/Login/LoginForm.tsx';
import RegisterForm from './pages/Login/Register/RegisterForm.tsx';
import PatientDashboardRoute from './pages/PatientDashboard/PatientDashboardRoute.tsx';
import PatientProfileForm from './pages/PatientProfile/PatientProfileForm.tsx';
import CollaboratorSurvey from './pages/Survey/CollaboratorSurvey.tsx';

const App: React.FC = inject()(
    observer(() => {
        const { currentUser } = useSessionProvider();
        const { open, close } = useNavProvider();

        const isLargeScreen = useMediaQuery('(min-width: 1024px)');

        useEffect(() => {
            if (isLargeScreen) open();
            else close();
        }, [isLargeScreen, open, close]);

        return (
            <Router>
                <EICFConsentGuard>
                <Routes>
                    <Route>
                        <Route path="login" element={<LoginForm />} />
                        <Route path="register" element={<RegisterForm />} />
                        <Route path="verify-email" element={<EmailVerification />} />
                        <Route path=":lang/verify-email" element={<EmailVerification />} />
                        <Route path="activate-account" element={<ActivateAccount />} />
                        <Route path=":lang/activate-account" element={<ActivateAccount />} />
                        <Route
                            path="resetPassword/*"
                            element={
                                <Shell>
                                    <ForgotPassword />
                                </Shell>
                            }
                        />
                        <Route path="changePassword" element={<ChangePassword />} />
                        <Route path="hcpProfile" element={<HcpForm />} />
                        <Route
                            path="patientProfile"
                            element={
                                <Shell>
                                    <PatientProfileForm />
                                </Shell>
                            }
                        />
                        <Route path="questionnaire" element={<CollaboratorSurvey />} />
                        <Route path="patientDashboard/*" element={<PatientDashboardRoute />} />
                        <Route path="adminDashboard/*" element={<AdminDashboardRoute />} />
                        <Route path="hcpPatientDashboard/*" element={<HcpPatientDashboardRoute />} />
                        <Route path="hcpDashboard/*" element={<HcpDashboardRoute />} />
                        <Route path="*" element={<NotFoundRedirect currentUser={currentUser} />} />
                    </Route>
                </Routes>
                </EICFConsentGuard>

                {/* PWA Install Prompt for iOS Safari users */}
                <PWAInstallPrompt />
                <SupportRequestButton />
                <WorktreeIndicator name={import.meta.env.VITE_WORKTREE_NAME} />
            </Router>
        );
    })
);

export default App;

const NotFoundRedirect: React.FC<{ currentUser: any }> = ({ currentUser }) => {
    let redirectPath = currentUser?.redirect;
    if (redirectPath?.startsWith('http') || redirectPath?.startsWith('https')) {
        try {
            const url = new URL(redirectPath);
            redirectPath = url.pathname + url.search + url.hash;
        } catch (e) {
            console.error(e);
            redirectPath = '/';
        }
    }

    if (redirectPath) 
        return <Navigate to={redirectPath} replace />;

    return <Navigate to="/login" replace />;
};
